/**
 * Users API
 *
 * Functions for interacting with the users endpoints.
 */

import { api } from './client';

// =============================================================================
// Types (re-exported from centralized models)
// =============================================================================

export type {
  UserRole,
  OrgRole,
  UserStatus,
  MemberState,
  ArchiveReason,
  User,
  UserDetail,
} from '../types/models';

import type { UserRole, MemberState, User, UserDetail } from '../types/models';

// =============================================================================
// Request/Response Types
// =============================================================================

/** Which slice of the roster to list. 'ALL' spans every state, for search. */
export type MemberStateFilter = MemberState | 'ALL';

/** Sortable crew-list columns. */
export type CrewSortBy = 'name' | 'lastLogin' | 'joined';
export type SortDir = 'asc' | 'desc';

export interface ListUsersParams {
  role?: UserRole;
  /** Membership state. Omitted means the active roster. */
  state?: MemberStateFilter;
  search?: string;
  sortBy?: CrewSortBy;
  sortDir?: SortDir;
  page?: number;
  limit?: number;
}

export interface ListUsersResponse {
  users: User[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface CreateUserInput {
  email: string;
  firstName: string;
  lastName: string;
  phone?: string | null;
  role?: UserRole;
}

export interface UpdateUserInput {
  email?: string;
  firstName?: string;
  lastName?: string;
  phone?: string | null;
  role?: UserRole;
  // No `status` here: the platform-wide account state is not an org admin's to
  // change. Archive and deactivate below are the per-org equivalents.
  // Payment tracking - crew default rates (in cents)
  defaultHourlyRate?: number | null;
  defaultDailyRate?: number | null;
}

// =============================================================================
// User CRUD
// =============================================================================

/**
 * List users with optional filters
 */
export async function listUsers(params?: ListUsersParams): Promise<ListUsersResponse> {
  const response = await api.get<ListUsersResponse>('/users', { params });
  return response.data;
}

/**
 * Get a single user by ID
 */
export async function getUser(id: string): Promise<{ user: UserDetail }> {
  const response = await api.get<{ user: UserDetail }>(`/users/${id}`);
  return response.data;
}

/**
 * Create a new user (send invite)
 */
export interface CreateUserResponse {
  message: string;
  user: User;
  generatedPassword?: string;
}

export async function createUser(data: CreateUserInput): Promise<CreateUserResponse> {
  const response = await api.post<CreateUserResponse>('/users', data);
  return response.data;
}

/**
 * Update a user
 */
export async function updateUser(
  id: string,
  data: UpdateUserInput
): Promise<{ message: string; user: User }> {
  const response = await api.put<{ message: string; user: User }>(`/users/${id}`, data);
  return response.data;
}

// =============================================================================
// Archive / Deactivate (both per-org, both profile-only)
// =============================================================================

/**
 * Archive a crew member or restore them to the roster.
 *
 * Archiving takes nothing away: they keep their login, still see published
 * events and can still set availability — and doing so restores them
 * automatically. It takes them off the roster, out of event notices and
 * digests, and out of the assignment picker.
 */
export async function setArchived(
  id: string,
  archived: boolean
): Promise<{ message: string; memberState: MemberState }> {
  const response = await api.patch<{ message: string; memberState: MemberState }>(
    `/users/${id}/archive`,
    { archived }
  );
  return response.data;
}

/**
 * Archive or restore many crew members at once (crew list bulk action).
 * Returns how many actually changed — admins, owners and members already in
 * the target state are skipped server-side.
 */
export async function bulkSetArchived(
  userIds: string[],
  archived: boolean
): Promise<{ message: string; changed: number; requested: number }> {
  const response = await api.post<{ message: string; changed: number; requested: number }>(
    '/users/bulk-archive',
    { userIds, archived }
  );
  return response.data;
}

/** What the bulk rate action sends: a key left out is left alone, null clears it. */
export interface BulkRatesInput {
  defaultHourlyRate?: number | null;
  defaultDailyRate?: number | null;
}

/**
 * Set default pay rates (cents) on many members of the current org at once.
 * Only active memberships of this org are touched; the rest are skipped and
 * the response says how many actually changed.
 */
export async function bulkSetRates(
  userIds: string[],
  rates: BulkRatesInput
): Promise<{ message: string; changed: number; requested: number }> {
  const response = await api.post<{ message: string; changed: number; requested: number }>(
    '/users/bulk-rates',
    { userIds, ...rates }
  );
  return response.data;
}

/**
 * Deactivate a member's access to THIS organization, or reactivate it.
 * Deactivating cancels their open assignments here. Their history stays, so
 * reports are unchanged, and nothing about their other orgs is touched.
 */
export async function setMembershipActive(
  id: string,
  isActive: boolean
): Promise<{ message: string; memberState: MemberState; cancelledAssignments?: number }> {
  const response = await api.patch<{
    message: string;
    memberState: MemberState;
    cancelledAssignments?: number;
  }>(`/users/${id}/membership`, { isActive });
  return response.data;
}

/**
 * Deactivate a user's membership in this org.
 * @deprecated Prefer setMembershipActive(id, false) — it reads as what it does
 * and can also reactivate.
 */
export async function deleteUser(id: string): Promise<{ message: string }> {
  const response = await api.delete<{ message: string }>(`/users/${id}`);
  return response.data;
}

/**
 * Resend invitation email to a user who hasn't logged in yet
 */
export async function resendInvite(id: string): Promise<{ message: string }> {
  const response = await api.post<{ message: string }>(`/users/${id}/resend-invite`);
  return response.data;
}

// =============================================================================
// Bulk Create Users
// =============================================================================

export interface BulkUserInput {
  email: string;
  firstName: string;
  lastName: string;
  phone?: string | null;
  role?: 'CREW' | 'ADMIN' | 'OWNER';
}

export interface BulkUserResult {
  email: string;
  firstName: string;
  lastName: string;
  status: 'created' | 'added_to_org' | 'skipped';
  skipReason?: string;
}

export interface BulkCreateUsersResponse {
  message: string;
  created: number;
  addedToOrg: number;
  skipped: number;
  users: BulkUserResult[];
}

export async function bulkCreateUsers(users: BulkUserInput[]): Promise<BulkCreateUsersResponse> {
  const response = await api.post<BulkCreateUsersResponse>('/users/bulk', { users });
  return response.data;
}

// =============================================================================
// Admin Password Operations
// =============================================================================

/**
 * Set a user's password (admin only)
 */
export async function setUserPassword(
  id: string,
  password: string
): Promise<{ message: string }> {
  const response = await api.post<{ message: string }>(
    `/users/${id}/set-password`,
    { password }
  );
  return response.data;
}

// =============================================================================
// Notification Status (admin diagnostic, read-only)
// =============================================================================

/**
 * Why a channel will not deliver. The server resolves this — the client must
 * never re-derive it from raw preferences JSON, because the defaults are
 * subtle (newEvents is all-off, legacy global keys outrank defaults) and the
 * two copies would drift.
 */
export type NotificationBlockReason =
  | 'ARCHIVED'
  | 'ORG_TIER'
  | 'ORG_SMS_OFF'
  | 'ORG_CATEGORY_OFF'
  | 'NO_CONSENT'
  | 'OPTED_OUT'
  | 'AWAITING_VERIFICATION'
  | 'NO_DEVICE'
  | 'PREF_OFF';

export interface NotificationChannelStatus {
  /** What the user chose, defaults resolved. */
  pref: boolean;
  /** Whether a message of this category would actually arrive right now. */
  deliverable: boolean;
  blockedBy: NotificationBlockReason | null;
}

export type SmsConsentState =
  | 'VERIFIED'
  | 'AWAITING_VERIFICATION'
  | 'OPTED_OUT'
  | 'CONTACT_PHONE_ONLY'
  | 'NONE';

export interface CrewNotificationStatus {
  sms: {
    state: SmsConsentState;
    maskedPhone: string | null;
    verifiedAt: string | null;
    consentedAt: string | null;
    optedOutAt: string | null;
    consentVersion: string | null;
  };
  push: { deviceCount: number; lastUsedAt: string | null };
  /** Roster standing, which explains any ARCHIVED blocks in the grid. */
  memberState: MemberState;
  org: {
    smsTierAllowed: boolean;
    smsNotificationsEnabled: boolean;
    notifyOnEventPublished: boolean;
    notifyOnEventChanges: boolean;
  };
  categories: Array<{
    key: string;
    label: string;
    /** null = the category does not offer this channel (renders as a dash). */
    email: NotificationChannelStatus | null;
    sms: NotificationChannelStatus | null;
    push: NotificationChannelStatus | null;
  }>;
  reminders: Array<{ timing: string; method: string }>;
}

/**
 * Read a crew member's notification settings for diagnosis. Admin-only and
 * read-only by design: SMS consent must come from the handset holder, and
 * channel preferences belong to the person.
 */
export async function getUserNotificationStatus(
  id: string
): Promise<CrewNotificationStatus> {
  const response = await api.get<CrewNotificationStatus>(
    `/users/${id}/notification-status`
  );
  return response.data;
}
